/**
 * useMotionPrecheck — «Kling, скорее всего, откажет» до генерации у Kling Motion Control (калибровка 12.10).
 *
 * Сервер (services/motion_precheck) меряет позу в той части ролика, что уйдёт в Kling: выбранная длительность, у
 * «По изображению» — до 10 с. Предупреждает только об узком: человек по пояс виден меньше 2 секунд подряд или в кадре
 * двое и больше. Ролик меряется один раз, а смена длины и ориентации отвечает из кэша сервера мгновенно.
 *
 * Это подсказка, а не проверка: ошибка сети, чужая модель или ролик не из нашего хранилища — предупреждения нет, запуск
 * ничего не ждёт. Пока ответ на новую длину идёт, прежнее предупреждение не показываем: оно было про другой отрезок.
 */
import { useEffect, useState } from 'react'
import type { Model } from '../../../data/models'
import { motionPrecheck, type MotionRiskWarning } from '../../../api/images'
import { isMotionControlModel } from '../utils/referenceLimits'

export interface MotionRisk extends MotionRiskWarning {
	/** Ролик, длительность и ориентация, к которым относится предупреждение: подтверждение запуска — на них. */
	key: string
}

const DEBOUNCE_MS = 400

/** Ключ проверки: ссылка без подписи (подпись меняется, ролик тот же), секунды, ориентация. */
export function motionRiskKey(videoUrl: string, seconds: number, orientation: string | undefined): string {
	return `${videoUrl.split('?')[0]}|${Math.round(seconds)}|${orientation ?? ''}`
}

export function useMotionPrecheck(
	model: Pick<Model, 'id' | 'backendModel'> | null | undefined,
	videoUrl: string | null | undefined,
	seconds: number,
	orientation: string | undefined,
): MotionRisk | null {
	const [risk, setRisk] = useState<MotionRisk | null>(null)
	const key = isMotionControlModel(model) && videoUrl ? motionRiskKey(videoUrl, seconds, orientation) : null

	useEffect(() => {
		if (!key || !videoUrl) return
		const ctrl = new AbortController()
		const timer = window.setTimeout(() => {
			motionPrecheck({ video_url: videoUrl, duration: Math.round(seconds), orientation }, ctrl.signal)
				.then((r) => {
					if (!ctrl.signal.aborted) setRisk(r?.warning ? { ...r.warning, key } : null)
				})
				.catch(() => {
					if (!ctrl.signal.aborted) setRisk(null)
				})
		}, DEBOUNCE_MS)
		return () => {
			ctrl.abort()
			window.clearTimeout(timer)
		}
		// videoUrl/seconds/orientation входят в key; подпись ссылки меняется без смены ролика — заново не спрашиваем.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key])

	return risk && risk.key === key ? risk : null
}
